import { giphyDarkestGrey, giphyLightestGrey } from '@giphy/colors'
import { SyntheticEvent, useContext, useState } from 'react'
import styled from 'styled-components'
import { BORDER_RADIUS_MEDIUM, SUPER_CUT_MIME_TYPES } from '../../../constants'
import { CreationToolsContext } from '../../../manager'
import DropZone from '../../drop-zone'
import { Text, TextSmall, TitleSmall } from '../../typography'
import CaptureModal from '../modals/new-capture'
import Inputs from './new-capture-inputs'

const Container = styled(DropZone)`
    background: ${giphyDarkestGrey};
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    box-sizing: border-box;
    height: 175px;
    margin-top: 30px;
    padding: 30px;
    position: relative;
`

const FlexBlock = styled.div`
    align-items: center;
    display: flex;
    width: 100%;
`

const FAQText = styled(TextSmall)`
    color: ${giphyLightestGrey};
    flex-shrink: 0;
    margin-left: 20px;

    a {
        color: inherit;
        text-decoration: underline;
    }

    i {
        display: inline-block;
        font-size: 14px;
        margin-left: 5px;
        vertical-align: middle;
    }
`

const Description = styled(Text)`
    margin-right: auto;
    max-width: none;
    width: 100%;
`

const Drop = styled.div`
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
`

const DropInput = styled.input`
    cursor: pointer;
    height: 100%;
    left: 0;
    margin: 0;
    opacity: 0;
    padding: 0;
    position: absolute;
    top: 0;
    width: 100%;
`

type Props = {
    onCaptureAdded?: (uuid: string) => void
}

const CreationSuperCutNewCapture = ({ onCaptureAdded }: Props) => {
    const { isDraggingFile } = useContext(CreationToolsContext)
    const [source, setSource] = useState<File | string>()

    const onFileInput = (e: SyntheticEvent<HTMLInputElement, Event>) => {
        const { files } = e.currentTarget
        if (files) {
            setSource(files[0])
            e.currentTarget.value = ''
        }
    }

    const onComplete = (uuid: string) => {
        setSource(undefined) // close modal
        onCaptureAdded && onCaptureAdded(uuid)
    }

    return (
        <>
            <Container disabled={!!source}>
                {isDraggingFile ? (
                    <Drop>
                        <TitleSmall>Drop File</TitleSmall>
                        <DropInput
                            {...{
                                type: 'file',
                                accept: SUPER_CUT_MIME_TYPES.join(','),
                                onInput: onFileInput,
                            }}
                        />
                    </Drop>
                ) : (
                    <>
                        <TitleSmall>New Capture</TitleSmall>
                        <FlexBlock>
                            <Description>We support your computer, YouTube, Vimeo, Vine and many others!</Description>
                            <FAQText>
                                Need Help?{' '}
                                <a
                                    href="https://support.giphy.com/hc/en-us/articles/360049349132"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Try our FAQ
                                </a>{' '}
                                <i className="ss-info" />
                            </FAQText>
                        </FlexBlock>
                        <Inputs onChange={setSource} />
                    </>
                )}
            </Container>
            {source && <CaptureModal source={source} onComplete={onComplete} onClose={() => setSource(undefined)} />}
        </>
    )
}

export default CreationSuperCutNewCapture
